import {
  CohortFilterGroup,
  CohortFilterGroupChildrenItem,
  CohortFilterGroupKind,
  CohortFilterGroupOp,
} from '@/api/generated/model';
import {
  DEFAULT_RULE_MAX_CONDITIONS_PER_LEVEL,
  DEFAULT_RULE_MAX_GROUP_NESTING_DEPTH,
  canGroupSelectedChildren as sharedCanGroupSelectedChildren,
  canSetGroupConnectorOp as sharedCanSetGroupConnectorOp,
  findRuleGroupById,
  findRuleGroupDepth,
  setGroupConnectorOp as sharedSetGroupConnectorOp,
  updateRuleGroupInTree,
  wrapContiguousChildren as sharedWrapContiguousChildren,
} from '@/comps/rule-builder/tree';
import { RuleGroupNode, RuleGroupOp } from '@/comps/rule-builder/types';
import { CohortUserFilter, createDefaultCohortUserFilterLeaf } from './cohort-user-filters';
import { normalizeCohortUserFilterForApi } from './cohort-user-filter-api';
import { isValidCohortUserFilter } from './cohort-user-filter-validation';

export type CohortRuleGroupOp = RuleGroupOp;

export type CohortRuleLeafNode = {
  id: string;
  kind: 'filter';
  filter: CohortUserFilter;
};

export type CohortRuleGroupNode = RuleGroupNode<CohortRuleLeafNode>;

export type CohortRuleNode = CohortRuleGroupNode | CohortRuleLeafNode;

export const COHORT_RULE_MAX_CONDITIONS_PER_LEVEL = DEFAULT_RULE_MAX_CONDITIONS_PER_LEVEL;
export const COHORT_RULE_MAX_GROUP_NESTING_DEPTH = DEFAULT_RULE_MAX_GROUP_NESTING_DEPTH;

let cohortRuleNodeCounter = 0;

export function createCohortRuleNodeId(): string {
  cohortRuleNodeCounter += 1;
  return `cohort-rule-node-${cohortRuleNodeCounter}`;
}

export function createDefaultFilterLeafNode(filter?: CohortUserFilter): CohortRuleLeafNode {
  return {
    id: createCohortRuleNodeId(),
    kind: 'filter',
    filter: filter ?? createDefaultCohortUserFilterLeaf(),
  };
}

export function createDefaultGroupNode(
  children?: CohortRuleNode[],
  op: CohortRuleGroupOp = 'and',
): CohortRuleGroupNode {
  return {
    id: createCohortRuleNodeId(),
    kind: 'group',
    op,
    children: children ?? [createDefaultFilterLeafNode()],
  };
}

export function createDefaultCohortRuleTree(): CohortRuleGroupNode {
  return createDefaultGroupNode();
}

export function canGroupSelectedChildren(
  parent: CohortRuleGroupNode,
  parentDepth: number,
  selectedIds: Set<string>,
  maxNestingDepth: number,
): boolean {
  return sharedCanGroupSelectedChildren(parent, parentDepth, selectedIds, maxNestingDepth, createDefaultGroupNode);
}

export function canSetGroupConnectorOp(
  group: CohortRuleGroupNode,
  childIndex: number,
  newOp: CohortRuleGroupOp,
  parentDepth: number,
  maxNestingDepth: number,
): boolean {
  return sharedCanSetGroupConnectorOp(group, childIndex, newOp, parentDepth, maxNestingDepth, createDefaultGroupNode);
}

export function setGroupConnectorOp(
  group: CohortRuleGroupNode,
  childIndex: number,
  newOp: CohortRuleGroupOp,
  parentDepth: number,
  maxNestingDepth: number,
): CohortRuleGroupNode {
  return sharedSetGroupConnectorOp(group, childIndex, newOp, parentDepth, maxNestingDepth, createDefaultGroupNode);
}

export function wrapContiguousChildren(
  parent: CohortRuleGroupNode,
  parentDepth: number,
  selectedIds: Set<string>,
  maxNestingDepth: number,
): CohortRuleGroupNode | null {
  return sharedWrapContiguousChildren(parent, parentDepth, selectedIds, maxNestingDepth, createDefaultGroupNode);
}

export function updateCohortRuleGroupInTree(
  node: CohortRuleGroupNode,
  groupId: string,
  updater: (group: CohortRuleGroupNode) => CohortRuleGroupNode,
): CohortRuleGroupNode {
  return updateRuleGroupInTree(node, groupId, updater);
}

export function findCohortRuleGroupById(node: CohortRuleGroupNode, groupId: string): CohortRuleGroupNode | null {
  return findRuleGroupById(node, groupId);
}

export function findCohortRuleGroupDepth(node: CohortRuleGroupNode, groupId: string, currentDepth = 0): number | null {
  return findRuleGroupDepth(node, groupId, currentDepth);
}

export function isCohortRuleTreeValid(tree: CohortRuleGroupNode): boolean {
  if (tree.children.length === 0) return false;

  return tree.children.every((child) => {
    if (child.kind === 'group') {
      return isCohortRuleTreeValid(child);
    }
    return isValidCohortUserFilter(child.filter);
  });
}

export function getInvalidCohortRuleNodeIds(tree: CohortRuleGroupNode): Set<string> {
  const invalid = new Set<string>();

  const visit = (node: CohortRuleGroupNode) => {
    if (node.children.length === 0) {
      invalid.add(node.id);
    }

    for (const child of node.children) {
      if (child.kind === 'group') {
        visit(child);
        continue;
      }
      if (!isValidCohortUserFilter(child.filter)) {
        invalid.add(child.id);
      }
    }
  };

  visit(tree);
  return invalid;
}

function treeNodeToApiChild(node: CohortRuleNode): CohortFilterGroupChildrenItem {
  if (node.kind === 'filter') {
    return normalizeCohortUserFilterForApi(node.filter);
  }

  return {
    kind: CohortFilterGroupKind.group,
    op: node.op === 'or' ? CohortFilterGroupOp.or : CohortFilterGroupOp.and,
    children: node.children.map(treeNodeToApiChild),
  };
}

export function cohortRuleTreeToFilterGroup(tree: CohortRuleGroupNode): CohortFilterGroup {
  return {
    kind: CohortFilterGroupKind.group,
    op: tree.op === 'or' ? CohortFilterGroupOp.or : CohortFilterGroupOp.and,
    children: tree.children.map(treeNodeToApiChild),
  };
}

function apiChildToTreeNode(child: CohortFilterGroupChildrenItem): CohortRuleNode {
  if (isCohortFilterGroup(child)) {
    return {
      id: createCohortRuleNodeId(),
      kind: 'group',
      op: child.op === CohortFilterGroupOp.or ? 'or' : 'and',
      children: (child.children ?? []).map(apiChildToTreeNode),
    };
  }

  return {
    id: createCohortRuleNodeId(),
    kind: 'filter',
    filter: child as CohortUserFilter,
  };
}

function isCohortFilterGroup(value: CohortFilterGroupChildrenItem): value is CohortFilterGroup {
  return (
    typeof value === 'object' &&
    value !== null &&
    !('column' in value) &&
    ('children' in value || ('kind' in value && value.kind === CohortFilterGroupKind.group) || 'op' in value)
  );
}

export function cohortFilterGroupToRuleTree(
  filters: CohortFilterGroup | CohortUserFilter[] | undefined | null,
): CohortRuleGroupNode {
  if (!filters) {
    return createDefaultCohortRuleTree();
  }

  if (Array.isArray(filters)) {
    return createDefaultGroupNode(
      filters.length > 0
        ? filters.map((filter) => createDefaultFilterLeafNode(filter as CohortUserFilter))
        : [createDefaultFilterLeafNode()],
    );
  }

  if (filters.children && filters.children.length > 0) {
    return {
      id: createCohortRuleNodeId(),
      kind: 'group',
      op: filters.op === CohortFilterGroupOp.or ? 'or' : 'and',
      children: filters.children.map(apiChildToTreeNode),
    };
  }

  return createDefaultCohortRuleTree();
}
